import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Shield, X } from '@phosphor-icons/react';

/**
 * Modal that age-verifies a user as 21+ (US adult). Used to gate the
 * 21+ Verified Universe sections.
 */
const AdultGate = ({ open, onClose, onVerified }) => {
  const [bd, setBd] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/verify-adult',
        { birthdate: bd, confirm_adult: confirm });
      toast.success('verified.');
      onVerified?.(data.user);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not verify');
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="adult-gate"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[140] bg-black/85 backdrop-blur-xl grid place-items-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.96 }}
            className="w-full max-w-md glass rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
              <X size={18} weight="bold" />
            </button>
            <div className="text-center mb-5">
              <Shield size={32} weight="duotone" className="text-bx-magenta mx-auto" />
              <div className="font-display font-bold text-2xl mt-3">Adult corner</div>
              <div className="text-xs text-white/55 mt-2 max-w-xs mx-auto">
                Some Bubbles drift through after-dark places — venues, late-night cities, intimate scenes.
                To enter, confirm you're 21+.
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">birthdate</div>
            <input type="date"
              data-testid="adult-bd"
              value={bd} onChange={(e) => setBd(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-bx-magenta mb-4" />

            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer mb-5">
              <input type="checkbox"
                data-testid="adult-confirm"
                checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I confirm I'm at least 21 and accept the adult terms.
            </label>

            <button
              data-testid="adult-verify"
              onClick={submit} disabled={busy || !bd || !confirm}
              className="w-full btn-volt py-3 rounded-full font-bold text-sm disabled:opacity-30">
              {busy ? 'verifying…' : 'Enter the after-dark world'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdultGate;
